import { z } from "zod";
import { modelPreferenceDefaultsSchema, modelPreferencesSchema } from "./modelPreferences.ts";

/**
 * Marker for a paused agent-to-agent scheduling-suggestion thread (BIG-4715).
 * Set when an OkThanks-looped-in inbound email spawns a "Do you want me to
 * handle this?" thread. The thread is created with `is_paused = true`, which
 * universally suppresses the AI workflow; this marker lets the source
 * pipeline and the form-submission handler recognize the thread as a paused
 * A2A suggestion so new inbound emails are filed-but-not-dispatched and the
 * single form artifact is kept last.
 */
export const a2aKickoffConfigSchema = z.object({
  externalThreadId: z.string(),
  /** Artifact id of the single yes/no userResponse form to keep at the bottom. */
  formArtifactId: z.string().optional(),
  /**
   * When a routine took the conversation over and retired this prompt
   * (BIG-6783). The consent question is spent from here on: the form must not
   * act if answered late, and the prompt must not be re-surfaced or retired
   * twice.
   *
   * Lives in `config` rather than being inferred from the thread's lifecycle,
   * because neither `status` nor `isPaused` can carry it. `status` gets rewritten
   * out from under the thread in both directions: submitting the form flips it to
   * `to-review` before the handler reads it, and a user archiving the prompt
   * unanswered marks it `completed` — so `completed` means both "retired" and
   * "the user swept it away with the question still open". `isPaused` stays true
   * on a retired thread on purpose — it is the BIG-4715 kill-switch, and nothing
   * in the lifecycle transitions clears it — but it can't be the marker either,
   * since a deliberate user action (a chat message, a form answer) does.
   */
  supersededAt: z.string().optional(),
});

export type A2aKickoffConfig = z.infer<typeof a2aKickoffConfigSchema>;

/**
 * Marker for a meeting-cancellation follow-up form. When a meeting scheduled
 * in a thread is cancelled, we re-surface the thread to `to-review` and post a
 * single "reschedule / reply / dismiss" question (see `applyMeetingCancellation`).
 * This holds the form artifact id so the form-submission handler can recognize
 * the cancellation gate and branch server-side (never delegating the choice to
 * the LLM), mirroring the `a2aKickoff` gate.
 */
export const cancellationKickoffConfigSchema = z.object({
  /** Artifact id of the reschedule/reply/dismiss userResponse form. */
  formArtifactId: z.string(),
});

/**
 * iCalUIDs whose cancellation this thread has already applied. The same
 * cancellation can be delivered on two channels (an ICS `CANCEL` email and a
 * calendar-sync `status === "cancelled"`); recording the id here makes
 * `applyMeetingCancellation` idempotent even when the thread holds no
 * `invitationReceived` artifact to mark `cancelled`.
 */
export const cancelledInvitationIdsSchema = z.array(z.string());

export type CancellationKickoffConfig = z.infer<typeof cancellationKickoffConfigSchema>;

/**
 * Descriptive marker on a thread opened to carry a group agent's request to a
 * participant's own agent (`peer.ASK` / `peer.DELEGATE`).
 *
 * **Nothing trusts this.** It is written by `openPeerRequest` for traces and for
 * anyone reading the thread row, and it decides nothing: the module set, the
 * guidance block and the completion hook all resolve a `peer_requests` row for
 * the account and thread instead. That is deliberate — `POST /threads/:id/config`
 * validates the whole of this schema and merges it, so any account can write this
 * key on a thread it owns, and a capability keyed on it would be a capability
 * anyone could grant themselves.
 *
 * `kind` mirrors `PEER_REQUEST_KINDS` in `schemas/peerRequests.ts`; it is spelled
 * again here because this file may not import outside `types/`.
 */
export const peerRequestConfigSchema = z.object({
  /** `peer_requests.id` — the row holding state, the timer, and both threads. */
  requestId: z.number(),
  kind: z.enum(["ask", "delegation"]),
  /** How the group was named to the participant, e.g. "Ski trip". */
  groupName: z.string(),
});

export type PeerRequestConfig = z.infer<typeof peerRequestConfigSchema>;

/**
 * Marker for a participant's per-account group thread whose conversation moved
 * onto a group agent. Set once, when the group is provisioned from an existing
 * conversation, and never cleared: the thread stays as that account's record,
 * but can no longer send and its agent no longer runs.
 *
 * Lives in `config` rather than `status` because status is rewritten from under
 * the thread — a composer message or a timer flips it to `to-review`, and a
 * user may unarchive it. Being superseded must survive all of those.
 */
export const groupUpgradeConfigSchema = z.object({
  /** ISO time the conversation moved onto its group agent. */
  supersededAt: z.string(),
  /** `groups.id` the conversation now lives on. */
  groupId: z.number(),
});

export type GroupUpgradeConfig = z.infer<typeof groupUpgradeConfigSchema>;

/**
 * Per-thread settings bag persisted at `threads.config`. Add new top-level
 * keys here as more per-thread settings appear; the column stays a single
 * typed jsonb so we don't need a migration per setting.
 */
export const threadConfigSchema = z.object({
  modelPreferences: modelPreferencesSchema.optional(),
  a2aKickoff: a2aKickoffConfigSchema.optional(),
  cancellationKickoff: cancellationKickoffConfigSchema.optional(),
  cancelledInvitationIds: cancelledInvitationIdsSchema.optional(),
  peerRequest: peerRequestConfigSchema.optional(),
  groupUpgrade: groupUpgradeConfigSchema.optional(),
});

export type ThreadConfig = z.infer<typeof threadConfigSchema>;

/** Whether the thread's conversation moved onto a group agent, leaving this thread read-only and inert. */
export function isThreadSupersededByGroup(thread: { config?: ThreadConfig | null }): boolean {
  return thread.config?.groupUpgrade !== undefined;
}

/**
 * Effective values when a thread hasn't overridden a setting. Mirrors the
 * shape of `ThreadConfig` so the FE can render `config[k] ?? defaults[k]`.
 */
export const threadConfigDefaultsSchema = z.object({
  modelPreferences: modelPreferenceDefaultsSchema,
});

export type ThreadConfigDefaults = z.infer<typeof threadConfigDefaultsSchema>;

export type UpdateThreadConfigResp = {
  config: ThreadConfig;
  defaults: ThreadConfigDefaults;
};
